<template>
  <div class="pending-packages-dialog">
    <h4 class="dialog-title warning">
      <i class="fal fa-exclamation-triangle"></i>
      ບໍ່ສາມາດພິມໄດ້
    </h4>

    <div class="warning-content">
      <div class="warning-icon">
        <i class="fal fa-box-open"></i>
      </div>
      <div class="warning-text">
        <p>{{ message }}</p>
        <p class="chute-info" v-if="chuteNumber">
          <i class="fal fa-inbox"></i>
          ຊ່ອງ: <strong>{{ String(chuteNumber).padStart(2, "0") }}</strong>
        </p>
      </div>
    </div>

    <div class="pending-packages-list">
      <div class="list-header">
        <h5 class="list-title">
          <i class="fal fa-barcode"></i>
          ພັດສະດຸທີ່ຍັງບໍ່ໄດ້ຢືນຢັນ
          <span class="count-badge">{{
            packages.length.toLocaleString()
          }}</span>
        </h5>
        <vs-button
          size="small"
          flat
          @click="copyToClipboard"
          :class="{ copied: copied }"
        >
          <i
            :class="copied ? 'fal fa-check' : 'fal fa-copy'"
            style="margin-right: 5px"
          ></i>
          {{ copied ? "ສຳເລັດ" : "ກ໋ອບປີ້" }}
        </vs-button>
      </div>
      <div class="packages-container">
        <span
          v-for="(pkg, index) in packages"
          :key="index"
          class="package-chip"
        >
          <i class="fal fa-box"></i>
          {{ pkg }}
        </span>
      </div>
      <!-- Hidden textarea for copy functionality -->
      <textarea
        class="hidden-textarea"
        readonly
        :value="packagesText"
        ref="packagesTextarea"
      ></textarea>
    </div>

    <div class="dialog-actions">
      <vs-button
        v-if="machineCode && chuteNumber"
        color="warning"
        :loading="isSkipping"
        :disabled="isSkipping"
        @click="skipPendingPackages"
      >
        <i class="fal fa-forward" style="margin-right: 5px"></i>
        ລ້າງລາຍການລໍຖ້າຢືນຢັນ
      </vs-button>
      <vs-button flat @click="$emit('close')">ປິດ</vs-button>
    </div>
  </div>
</template>

<script>
export default {
  name: "PendingPackagesErrorDialog",

  props: {
    packages: {
      type: Array,
      default: () => [],
    },
    message: {
      type: String,
      default: "ມີພັດສະດຸທີ່ຍັງບໍ່ໄດ້ຢືນຢັນການຄັດແຍກ",
    },
    chuteNumber: {
      type: [Number, String],
      default: null,
    },
    machineCode: {
      type: String,
      default: null,
    },
    phase: {
      type: String,
      default: null,
    },
  },

  data() {
    return {
      copied: false,
      isSkipping: false,
    };
  },

  computed: {
    packagesText() {
      return this.packages.join("\n");
    },
  },

  methods: {
    async copyToClipboard() {
      try {
        await navigator.clipboard.writeText(this.packagesText);
        this.copied = true;
        setTimeout(() => {
          this.copied = false;
        }, 2000);
      } catch (err) {
        // Fallback for older browsers
        this.$refs.packagesTextarea.select();
        document.execCommand("copy");
        this.copied = true;
        setTimeout(() => {
          this.copied = false;
        }, 2000);
      }
    },

    async skipPendingPackages() {
      if (!this.machineCode || !this.chuteNumber) return;

      this.isSkipping = true;
      try {
        const payload = {
          machineCode: this.machineCode,
          chuteCode: parseInt(this.chuteNumber),
        };

        // Add phase if provided
        if (this.phase) {
          payload.phase = this.phase;
        }

        const response = await this.$http.post(
          "sorting/admin/skip-pending-packages",
          payload
        );

        if (response.data.success) {
          const data = response.data.data;
          this.$notification.OpenNotification_EditItem_OnSuccess(
            "top-right",
            "dark",
            3000
          );

          // Emit skip event with the response data so parent can handle refresh
          this.$emit("skip-success", {
            packagesMoved: data.packages_moved,
            errorChuteNumber: data.error_chute_number,
            billNumbers: data.bill_numbers,
          });

          // Close dialog after successful skip
          this.$emit("close");
        }
      } catch (error) {
        const errorMessage =
          error.response?.data?.message || "ເກີດຂໍ້ຜິດພາດໃນການຍ້າຍພັດສະດຸ";
        this.$notification.error({ message: errorMessage });
      } finally {
        this.isSkipping = false;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.pending-packages-dialog {
  padding: 10px;
}

.dialog-title {
  font-weight: bold;
  font-size: 18px;
  margin-bottom: 20px;

  &.warning {
    color: #f57c00;
  }

  i {
    margin-right: 8px;
  }
}

.warning-content {
  display: flex;
  align-items: flex-start;
  gap: 15px;
  padding: 15px;
  background: #fff3e0;
  border-radius: 10px;
  border-left: 4px solid #f57c00;
  margin-bottom: 20px;
}

.warning-icon {
  width: 50px;
  height: 50px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #ffe0b2;
  border-radius: 50%;
  flex-shrink: 0;

  i {
    font-size: 24px;
    color: #f57c00;
  }
}

.warning-text {
  flex: 1;

  p {
    margin: 0;
    color: #333;
    font-size: 14px;
    line-height: 1.5;
  }

  .chute-info {
    margin-top: 8px;
    padding: 6px 10px;
    background: #e3f2fd;
    border-radius: 6px;
    color: #1565c0;
    font-size: 13px;

    i {
      margin-right: 5px;
    }

    strong {
      font-weight: 700;
    }
  }
}

.pending-packages-list {
  .list-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 12px;
  }

  .list-title {
    font-size: 14px;
    font-weight: 600;
    color: #333;
    margin: 0;
    display: flex;
    align-items: center;
    gap: 8px;

    i {
      color: #f57c00;
    }

    .count-badge {
      background: #f57c00;
      color: white;
      padding: 2px 8px;
      border-radius: 12px;
      font-size: 12px;
      font-weight: 600;
    }
  }

  .copied {
    color: #4caf50 !important;
  }
}

.packages-container {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  max-height: 180px;
  overflow-y: auto;
  padding: 12px;
  background: #fafafa;
  border: 1px solid #e0e0e0;
  border-radius: 8px;

  .package-chip {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    padding: 4px 10px;
    background: #fff;
    border: 1px solid #ffe0b2;
    border-radius: 16px;
    font-size: 12px;
    font-family: monospace;
    font-weight: 500;
    color: #e65100;
    white-space: nowrap;

    i {
      font-size: 10px;
      color: #f57c00;
    }

    &:hover {
      background: #fff3e0;
      border-color: #f57c00;
    }
  }
}

.hidden-textarea {
  position: absolute;
  left: -9999px;
  opacity: 0;
}

.dialog-actions {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  margin-top: 20px;
  padding-top: 15px;
  border-top: 1px solid #eee;
}
</style>
